import React, {memo, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {Link, useNavigate} from "react-router-dom";
import {Box, Button, Card, Typography, useTheme} from "@mui/material";
import UserFilterButton from "./UserFilter";
import TagChips from "./TagChips";
import dayjs from "dayjs";
import {generateChips} from "../utils/dataTransformers";
import {useRegScoutCore} from "../../../resources/RegScoutCoreProvider";
import {useRegulationsStyles} from "./styles/Styles";
import {useDetailStyles} from "./styles/detailStyles";
import FakeButton from "../../../components/upgrade/FakeButton";

const ListCard = memo(
    ({
      regulation,
      customList,
      customNavigateState,
      upgradeRequired = false,
      areFiltersActive,
      hideFilteredItems,
      setCurrentFilters,
    }) => {
      const navigate = useNavigate();
      const {classes, cx} = useRegulationsStyles();
      const {classes: detailClasses} = useDetailStyles();
      const {isPlatform} = useRegScoutCore();
      const theme = useTheme();

      if (hideFilteredItems && regulation.filtered && !upgradeRequired) return null;

      const chips = generateChips(regulation);
      const classIfFiltered =
      upgradeRequired ? detailClasses.upgradeContainer :
      regulation.filtered ? classes.filteredCard :
      "";
      const sinceIfEffective = dayjs().isAfter(dayjs(regulation.effectiveAt)) ? "since " : "";
      const effectiveSince =
      regulation.effectiveAt ?
        `- Effective ${sinceIfEffective}${dayjs(regulation.effectiveAt).format("LL")}` :
      "";
      const path = !isPlatform ? "/regulation" : "/compliance/research/regulation";
      const regPath = `${path}/${regulation.id}`;
      const handleClick = useCallback(() => {
        navigate(regPath, {state: customNavigateState});
      }, [navigate, regPath, customNavigateState]);
      const cardSx = useMemo(
          () => (upgradeRequired ? {cursor: "default !important"} : undefined),
          [upgradeRequired],
      );

      return (
        <Card
          className={classes.card}
          elevation={!areFiltersActive || (regulation.filtered && !upgradeRequired) ? 1 : 3}
          onClick={upgradeRequired ? undefined : handleClick}
          sx={cardSx}
        >
          <Box className={upgradeRequired ? detailClasses.upgradeContainer : undefined}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" className={cx(classes.cardHeader, classIfFiltered)}>
                  {regulation.legalType} {effectiveSince}
                </Typography>
                {upgradeRequired ?
                <FakeButton label="Exclude" color="neutral" className={classes.userFilterButton} /> :
              !customList ?
                <UserFilterButton
                  regulation={regulation}
                  isFiltered={regulation.filtered}
                  setCurrentFilters={setCurrentFilters}
                /> :
              null}
              </Box>
              <Typography variant="h5" sx={{opacity: regulation.filtered ? 0.6 : undefined}}>
                {regulation.regulator}
              </Typography>
            </Box>
            <Box py={1} className={classIfFiltered}>
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{__html: regulation?.descriptionDisplay}}
                className={classes.description}
                sx={(theme) => ({color: theme.palette.text.primary})}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={1} className={classIfFiltered}>
            <Box>
              <Typography variant="subtitle">{regulation?.deadlineDisplay}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={1}>
                <TagChips chips={chips} disableHover={upgradeRequired} />
              </Box>
              {upgradeRequired ?
              <FakeButton
                label="View"
                variant="contained"
                size="medium"
                className={classes.viewButton}
                backgroundColor={theme.palette.primary.main}
              /> :
            <Button
              to={`${path}/${regulation.id}`}
              component={Link}
              variant="contained"
              className={classes.viewButton}
              disableRipple
            >
                View
            </Button>
              }
            </Box>
          </Box>
        </Card>
      );
    },
    (prev, next) =>
      prev.regulation.id === next.regulation.id &&
    prev.regulation.filtered === next.regulation.filtered &&
    prev.hideFilteredItems === next.hideFilteredItems,
);

ListCard.propTypes = {
  areFiltersActive: PropTypes.bool,
  customList: PropTypes.array,
  customNavigateState: PropTypes.object,
  hideFilteredItems: PropTypes.bool,
  regulation: PropTypes.object,
  upgradeRequired: PropTypes.bool,
};

export default ListCard;
